import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { isEqual, uniq } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { UserProfileActions } from './user-profile.actions';
import { UserProfileSelectors } from './user-profile.selectors';
import { UserProfileService } from '../../features/settings/services/user-profile.service';
import { SemerisRoutes } from '../../features/shared/constants/routes';
import { WebSocketEntities } from '../../features/shared/models/web-socket/web-socket.models';
import { createWebSocketEffect } from '../../features/shared/services/web-socket/create-web-socket-effect';
import { WebSocketService } from '../../features/shared/services/web-socket/web-socket.service';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { AppActions } from '../app/app.actions';
import { RootState } from '../root-state.model';

@Injectable()
export class UserProfileEffects {
  private readonly service = inject(UserProfileService);
  private readonly webSocketService = inject(WebSocketService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);

  wsEffect$: Observable<Action> = createWebSocketEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.AssetSetting,
    () => UserProfileActions.RefreshAssetSetting()
  );

  refreshAssetSettingsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.RefreshAssetSetting),
      switchMap(() =>
        this.service.refreshAssetSettings().pipe(
          map((assetSetting) => UserProfileActions.RefreshAssetSettingSuccess({ assetSetting })),
          reportEffectError(UserProfileActions.RefreshAssetSettingFail)
        )
      )
    )
  );

  loadProfileEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.LoadProfile),
      mergeMap((action) => {
        console.debug(`[UserProfileActions.LoadProfile] received`);
        return this.service.getProfile().pipe(
          tap(() => {
            console.debug(`[UserProfileActions.LoadProfile] profile fetched`);
          }),
          map((profile) => {
            console.debug(`[UserProfileActions.LoadProfile] dispatching [UserProfileActions.LoadProfileSuccess]`);
            return UserProfileActions.LoadProfileSuccess({
              profile,
              init: action.init,
              selectedAssetShortName: profile.assetShortNames || profile.availableAssetShortNames,
            });
          }),
          reportEffectError(UserProfileActions.LoadProfileFail)
        );
      })
    )
  );

  updateProfileEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateProfile),
      withLatestFrom(
        this.store.select(UserProfileSelectors.impersonatedTenant),
        this.store.select(UserProfileSelectors.impersonatedUser)
      ),
      switchMap(([action, impersonatedTenant, impersonatedUser]) =>
        this.service.updateProfile(action.profile).pipe(
          map((profile) =>
            UserProfileActions.UpdateProfileSuccess({
              profile,
              reloadApp:
                impersonatedTenant !== profile.impersonatedTenant || impersonatedUser !== profile.impersonatedUser,
            })
          ),
          reportEffectError(UserProfileActions.UpdateProfileFail)
        )
      )
    )
  );

  updateAssetSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateAssetSetting),
      withLatestFrom(this.store),
      concatMap(([action, store]) =>
        this.service.updateAssetSettings(action.setting).pipe(
          map(() => UserProfileActions.UpdateAssetSettingSuccess()),
          reportEffectError(UserProfileActions.UpdateAssetSettingFail)
        )
      )
    )
  );

  updateDatapointCompareSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.SetDatapointCompareSettings),
      withLatestFrom(this.store),
      concatMap(([action, store]) =>
        this.service
          .updateUserSettings({
            datapointCompareSettings: {
              showCategories: action.datapointCompareSettings?.showCategories,
              showDetails: action.datapointCompareSettings?.showDetails,
              sort: {
                active: action.datapointCompareSettings?.sort?.active,
                direction: action.datapointCompareSettings?.sort?.direction,
              },
            },
          })
          .pipe(
            map(() =>
              UserProfileActions.SetDatapointCompareSettingsSuccess({
                datapointCompareSettings: action.datapointCompareSettings,
              })
            ),
            reportEffectError(UserProfileActions.SetDatapointCompareSettingsFail)
          )
      )
    )
  );

  updateAssetSettingRecord$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateAssetSettingRecord),
      withLatestFrom(this.store),
      concatMap(([action, store]) =>
        this.service.updateAssetSettingRecord(action.record).pipe(
          map(() => UserProfileActions.UpdateAssetSettingRecordSuccess()),
          reportEffectError(UserProfileActions.UpdateAssetSettingRecordFail)
        )
      )
    )
  );

  unlockEntity$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UnlockEntity),
      withLatestFrom(this.store),
      concatMap(([action, store]) =>
        this.service.unlockEntity(action.entityId).pipe(
          map((unlockedEntities) =>
            UserProfileActions.UnlockEntitySuccess({
              unlockedEntities,
            })
          ),
          reportEffectError(UserProfileActions.UnlockEntityFail)
        )
      )
    )
  );

  updateCompletedTours$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.ToggleCompletedTours),
      withLatestFrom(this.store),
      concatMap(([action, store]) => {
        const list = [...(store.userProfile.completedTourIds || [])];

        if (!action.completed && list.includes(action.tourId)) {
          list.splice(list.indexOf(action.tourId), 1);
        } else if (action.completed) {
          list.push(action.tourId);
        }

        return this.service.updateCompletedTourList(uniq(list)).pipe(
          map((tourIds) =>
            UserProfileActions.ToggleCompletedToursSuccess({
              tourIds,
            })
          ),
          reportEffectError(UserProfileActions.ToggleCompletedToursFail)
        );
      })
    )
  );

  updateMultipleAssetSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateMultipleAssetSetting),
      concatMap((action) =>
        this.service.updateMultipleAssetSettings(action.settings).pipe(
          map(() => UserProfileActions.UpdateMultipleAssetSettingSuccess({ id: action.id })),
          reportEffectError(UserProfileActions.UpdateMultipleAssetSettingFail)
        )
      )
    )
  );

  updateAssetEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateAsset),
      switchMap((action) =>
        this.service.updateAsset(action.selectedAssetShortNames).pipe(
          map((assetShortNames) =>
            UserProfileActions.UpdateAssetSuccess({
              assetShortNames,
              redirectTo: action.redirectTo,
              title: action.title,
            })
          ),
          catchError((error) => of(UserProfileActions.UpdateAssetFail({ error })))
        )
      )
    )
  );

  resetPasswordEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.ResetPassword),
      switchMap((action) =>
        this.service.resetPassword().pipe(
          map(() => UserProfileActions.ResetPasswordSuccess()),
          reportEffectError(UserProfileActions.ResetPasswordFail)
        )
      )
    )
  );

  resetPassworSuccessdEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.ResetPasswordSuccess),
      map((action) =>
        AppActions.PushNotification({
          message: 'Password reset initiated',
          config: {
            duration: 2_000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
          },
        })
      )
    )
  );

  sendVerificationEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.SendVerificationMail),
      switchMap((action) =>
        this.service.verifyEmail().pipe(
          map(() => UserProfileActions.SendVerificationMailSuccess()),
          reportEffectError(UserProfileActions.SendVerificationMailFail)
        )
      )
    )
  );

  sendVerificationSuccessdEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.SendVerificationMailSuccess),
      map((action) =>
        AppActions.PushNotification({
          message: 'Verification mail sent',
          config: {
            duration: 2_000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
          },
        })
      )
    )
  );

  updateSuccessdEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateProfileSuccess),
      map((action) =>
        AppActions.PushNotification({
          message: 'Profile updated',
          config: {
            duration: 2_000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
          },
        })
      )
    )
  );

  updateSuccessdTokenRefreshEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateProfileSuccess),
      filter((action) => !!action.reloadApp),
      map(() => AppActions.GetNewTokenAndReload())
    )
  );

  triggerAppInitFailEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserProfileActions.LoadProfileSuccess),
        filter((action) => action.init && !action.selectedAssetShortName && action.init === 'welcome'),
        map(() => this.router.navigate(['/', SemerisRoutes.welcome]))
      ),
    { dispatch: false }
  );

  updateAssetSuccessEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserProfileActions.UpdateAssetSuccess),
        map((action) =>
          this.router.navigate(['/', SemerisRoutes.asset.changeAsset], {
            queryParams: {
              redirectTo: action.redirectTo,
              assetShortNames: action.assetShortNames,
              title: action.title,
            },
          })
        )
      ),
    { dispatch: false }
  );

  refreshColumnsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.RefreshAssetSettingSuccess),
      map((action) => action.assetSetting?.columns),
      withLatestFrom(this.store),
      filter(([columns, store]) => !!columns && !isEqual(columns, store.userProfile.columns)),
      map(([columns, store]) => UserProfileActions.SetColumns({ columns }))
    )
  );

  updatePositionTableHiddenColumns$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdatePositionTableHiddenColumns),
      switchMap(({ columns }) =>
        this.service.updatePositionTableHiddenColumns(columns).pipe(
          map(() => UserProfileActions.UpdatePositionTableHiddenColumnsSuccess({ columns })),
          reportEffectError(UserProfileActions.UpdatePositionTableHiddenColumnsFail)
        )
      )
    )
  );

  updatePositionTableHiddenColumnsFailEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdatePositionTableHiddenColumnsFail),
      map(() =>
        AppActions.PushNotification({
          message: 'Failed to update table column visibility',
          config: { duration: 2_000 },
        })
      )
    )
  );

  updatePositionFiltersSuccessEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdatePositionFilters),
      switchMap(({ filters }) =>
        this.service.updatePositionFilters(filters).pipe(
          map(() => UserProfileActions.UpdatePositionFiltersSuccess({ filters })),
          reportEffectError(UserProfileActions.UpdatePositionFiltersFail)
        )
      )
    )
  );

  updatePositionFiltersFailEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdatePositionFiltersFail),
      map(() =>
        AppActions.PushNotification({
          message: 'Failed to update position filters',
          config: { duration: 2_000 },
        })
      )
    )
  );

  updateTransactionTaskSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateTransactionTaskSettings),
      switchMap(({ settings }) =>
        this.service.updateTransactionTaskSettings(settings).pipe(
          map(() => UserProfileActions.UpdateTransactionTaskSettingsSuccess({ settings })),
          reportEffectError(UserProfileActions.UpdateTransactionTaskSettingsFail)
        )
      )
    )
  );

  updateTransactionTaskSettingsFailEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateTransactionTaskSettingsFail),
      map(() =>
        AppActions.PushNotification({
          message: 'Failed to update deal queue settings',
          config: { duration: 2_000 },
        })
      )
    )
  );

  setFavoriteDatapointFieldIds$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.SetFavoriteDatapointFieldIds),
      concatMap(({ favoriteDatapointFieldIds }) =>
        this.service.updateUserSettings({ favoriteDatapointFieldIds }).pipe(
          map(() => UserProfileActions.SetFavoriteDatapointFieldIdsSuccess({ favoriteDatapointFieldIds })),
          reportEffectError(UserProfileActions.SetFavoriteDatapointFieldIdsFail)
        )
      )
    )
  );
}
